import { SyntheticEvent, useEffect, useState } from "react";
import * as documentRepositoryAttachmentsActionCreators from '../actions/attachments';
import { IFilter } from "../types/filter";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { useTranslation } from "react-i18next";


const ALL_ATTACHMENTS_CATEGORY = '__all__';
type RawDocumentRepositoryAttachment = Record<string, unknown> & {
  id: string;
  attachment_id?: string;
  associated_object?: string;
  contract_names?: string[];
  created_date?: string;
  energy_types?: string[];
};

const EMPTY_ATTACHMENTS_BY_CONTRACT: Record<string, RawDocumentRepositoryAttachment[]> = {};
const EMPTY_CATEGORIES: DocumentRepositoryCategory[] = [];
type DocumentRepositoryCategory = {
  code: string;
  name: string;
  count?: number;
};

type DocumentRepositoryAttachment = {
  id: string;
  name?: string;
  filename?: string;
  created_date?: string;
  category_code?: string;
  category_name?: string;
  associated_object?: string;
  associated_objects?: string[];
  file_size?: number;
  size_text?: string;
  contract_name: string;
  contract_names?: string[];
  energy_type: string;
  energy_types?: string[];
  created_date_sort?: string;
  rowId: string;
};

const buildCategoryFilter = (categoryCode: string) => {
  if (!categoryCode || categoryCode === ALL_ATTACHMENTS_CATEGORY) {
    return '';
  }

  const escapedCategoryCode = categoryCode.replace(/'/g, "\\'");
  return `('category_code', 'in', ['${escapedCategoryCode}'])`;
};

const buildRequestFilters = (baseFilters = '', categoryCode = ALL_ATTACHMENTS_CATEGORY) => {
  return [baseFilters, buildCategoryFilter(categoryCode)]
    .filter(Boolean)
    .join(', ');
};

const isNonEmptyString = (value: string | undefined): value is string =>
  Boolean(value);

const aggregateAttachments = (
  attachments: DocumentRepositoryAttachment[]
): DocumentRepositoryAttachment[] => {
  const attachmentsById = new Map<string, DocumentRepositoryAttachment>();

  attachments.forEach((attachment) => {
    const existingAttachment = attachmentsById.get(attachment.id);

    if (!existingAttachment) {
      attachmentsById.set(attachment.id, {
        ...attachment,
        associated_objects: attachment.associated_object ? [attachment.associated_object] : [],
        contract_names: attachment.contract_name ? [attachment.contract_name] : [],
        energy_types: attachment.energy_type ? [attachment.energy_type] : [],
        rowId: attachment.id,
      });
      return;
    }

    const mergedAssociatedObjects = Array.from(new Set([
      ...(existingAttachment.associated_objects || []),
      attachment.associated_object,
    ].filter(isNonEmptyString))).sort();
    const mergedContractNames = Array.from(new Set([
      ...(existingAttachment.contract_names || []),
      attachment.contract_name,
    ].filter(isNonEmptyString))).sort();
    const mergedEnergyTypes = Array.from(new Set([
      ...(existingAttachment.energy_types || []),
      attachment.energy_type,
    ].filter(isNonEmptyString))).sort();

    attachmentsById.set(attachment.id, {
      ...existingAttachment,
      associated_objects: mergedAssociatedObjects,
      associated_object: mergedAssociatedObjects[0] || '',
      contract_names: mergedContractNames,
      contract_name: mergedContractNames[0] || '',
      energy_types: mergedEnergyTypes,
      energy_type: mergedEnergyTypes.join(', '),
      rowId: attachment.id,
    });
  });

  return Array.from(attachmentsById.values()).sort((leftAttachment, rightAttachment) => {
    const leftDate = leftAttachment.created_date_sort
      ? new Date(leftAttachment.created_date_sort).getTime()
      : 0;
    const rightDate = rightAttachment.created_date_sort
      ? new Date(rightAttachment.created_date_sort).getTime()
      : 0;

    return rightDate - leftDate;
  });
};


const formatCreatedDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('ca-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const processFlatAttachments = (
  attachments: RawDocumentRepositoryAttachment[]
): DocumentRepositoryAttachment[] =>
  (attachments || []).map((att) => ({
    ...att,
    rowId: String(att.attachment_id || att.id),
    contract_names: att.contract_names || [],
    contract_name: (att.contract_names || [])[0] || '',
    energy_types: att.energy_types || [],
    energy_type: (att.energy_types || []).join(', '),
    associated_objects: att.associated_object ? [att.associated_object] : [],
    created_date_sort: att.created_date,
    created_date: formatCreatedDate(att.created_date),
  }));


export const useDocumentRepository = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Referencies estables per als selectors: evitar crear objectes nous a cada
  // render quan la clau encara no existeix al state (bucle de re-render).
  const flatAttachments = useAppSelector(state => state.documentRepository.flatAttachments);
  const electricityAttachments = useAppSelector(state => state.documentRepository.electricityAttachments ?? EMPTY_ATTACHMENTS_BY_CONTRACT);
  const gasAttachments = useAppSelector(state => state.documentRepository.gasAttachments ?? EMPTY_ATTACHMENTS_BY_CONTRACT);
  const categories = (useAppSelector(
    state => state.documentRepository.categories ?? EMPTY_CATEGORIES
  ) as DocumentRepositoryCategory[]);
  const itemsOffset = useAppSelector(state => state.documentRepository.itemsOffset);
  const itemsPage = useAppSelector(state => state.documentRepository.itemsPage);
  const loaded = useAppSelector(state => state.documentRepository.loaded);
  const errors = useAppSelector(state => state.documentRepository.errors);
  const currentFilter = useAppSelector(state => state.documentRepository.filters.currentFilter);
  const token = useAppSelector(state => state.auth.token);

  const [initialFilter, setInitialFilter] = useState<IFilter[]>([]);
  const [initialItemsPage, setInitialItemsPage] = useState<number | null>(10);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<string[]>([]);
  const [activeCategoryCode, setActiveCategoryCode] = useState(ALL_ATTACHMENTS_CATEGORY);

  const processAttachments = (
    source: Record<string, RawDocumentRepositoryAttachment[]>,
    type: string
  ): DocumentRepositoryAttachment[] =>
    Object.entries(source || {}).flatMap(([contractName, attachments]) =>
      attachments.map((attachment, index) => ({
        ...attachment,
        contract_name: contractName,
        energy_type: type,
        created_date_sort: attachment.created_date,
        rowId: [
          type,
          contractName,
          attachment.id,
          attachment.associated_object,
          attachment.created_date,
          index
        ].join('::'),
        created_date: formatCreatedDate(attachment.created_date),
      }))
    );

  const items = flatAttachments
    ? processFlatAttachments(flatAttachments)
    : aggregateAttachments([
        ...processAttachments(electricityAttachments, 'electricity'),
        ...processAttachments(gasAttachments, 'gas')
      ]);
  const categoryTabs = [
    {
      code: ALL_ATTACHMENTS_CATEGORY,
      name: t('common:text.document_repository_tab_all_attachments')
    },
    ...categories
  ];

  const selectedAttachments = items.filter(
    (attachment) => selectedAttachmentIds.includes(attachment.rowId)
  );
  const hasVisibleAttachments = items.length > 0;
  const hasSelectedAttachments = selectedAttachments.length > 0;
  const isAllVisibleSelected = hasVisibleAttachments && selectedAttachments.length === items.length;

  useEffect(() => { //Runs when component is mounted/unmounted
    dispatch(documentRepositoryAttachmentsActionCreators.fetchFilters(token));
  }, []);

  useEffect(() => {
    setInitialFilter([]);
    setSelectedAttachmentIds([]);
    dispatch(
      documentRepositoryAttachmentsActionCreators.changeFilter('', 10, 0)
    );
    dispatch(
      documentRepositoryAttachmentsActionCreators.fetchDocumentRepositoryAttachments(
        token,
        false,
        buildRequestFilters('', ALL_ATTACHMENTS_CATEGORY)
      )
    );
  }, []);

  useEffect(() => {
    setSelectedAttachmentIds((currentSelection) =>
      currentSelection.filter((attachmentId) =>
        items.some((attachment) => attachment.rowId === attachmentId)
      )
    );
  }, [flatAttachments, electricityAttachments, gasAttachments]);

  useEffect(() => {
    if (
      activeCategoryCode !== ALL_ATTACHMENTS_CATEGORY &&
      !categories.some((category) => category.code === activeCategoryCode)
    ) {
      setActiveCategoryCode(ALL_ATTACHMENTS_CATEGORY);
      setSelectedAttachmentIds([]);
      dispatch(
        documentRepositoryAttachmentsActionCreators.changeFilter(
          currentFilter,
          itemsPage,
          0
        )
      );
      dispatch(
        documentRepositoryAttachmentsActionCreators.fetchDocumentRepositoryAttachments(
          token,
          false,
          buildRequestFilters(currentFilter, ALL_ATTACHMENTS_CATEGORY),
          0,
          itemsPage
        )
      );
    }
  }, [categories]);

  const fetchData = () => {
    setSelectedAttachmentIds([]);
    dispatch(
      documentRepositoryAttachmentsActionCreators.fetchDocumentRepositoryAttachments(
        token,
        false,
        buildRequestFilters(currentFilter, activeCategoryCode),
        itemsOffset,
        itemsPage,
        true
      )
    );
  };

  const applySearchFilter = (newFilters: string, newItemsPage: number) => {
    const newItemsOffset = newItemsPage !== itemsPage ? 0 : itemsOffset;
    const itemsPageLimit = newItemsPage ?? 10;
    setInitialItemsPage(newItemsPage);
    setSelectedAttachmentIds([]);
    dispatch(
      documentRepositoryAttachmentsActionCreators.changeFilter(
        newFilters,
        itemsPageLimit,
        newItemsOffset
      )
    );
    dispatch(
      documentRepositoryAttachmentsActionCreators.fetchDocumentRepositoryAttachments(
        token,
        false,
        buildRequestFilters(newFilters, activeCategoryCode),
        newItemsOffset,
        itemsPageLimit
      )
    );
  };

  const handleItemsOffset = (newOffset: number) => {
    setSelectedAttachmentIds([]);
    dispatch(
      documentRepositoryAttachmentsActionCreators.changeFilter(
        currentFilter,
        itemsPage,
        newOffset
      )
    );
    dispatch(
      documentRepositoryAttachmentsActionCreators.fetchDocumentRepositoryAttachments(
        token,
        false,
        buildRequestFilters(currentFilter, activeCategoryCode),
        newOffset,
        itemsPage
      )
    );
  };

  const toggleAttachmentSelection = (attachmentId: string) => {
    setSelectedAttachmentIds((currentSelection) =>
      currentSelection.includes(attachmentId)
        ? currentSelection.filter((selectedId) => selectedId !== attachmentId)
        : [...currentSelection, attachmentId]
    );
  };

  const toggleAllVisibleSelection = () => {
    setSelectedAttachmentIds((currentSelection) =>
      currentSelection.length === items.length ? [] : items.map((attachment) => attachment.rowId)
    );
  };

  const handleCategoryChange = (_event: SyntheticEvent, newCategoryCode: string) => {
    const nextCategoryCode = newCategoryCode || ALL_ATTACHMENTS_CATEGORY;
    setActiveCategoryCode(nextCategoryCode);
    setSelectedAttachmentIds([]);
    dispatch(
      documentRepositoryAttachmentsActionCreators.changeFilter(
        currentFilter,
        itemsPage,
        0
      )
    );
    dispatch(
      documentRepositoryAttachmentsActionCreators.fetchDocumentRepositoryAttachments(
        token,
        false,
        buildRequestFilters(currentFilter, nextCategoryCode),
        0,
        itemsPage
      )
    );
  };

  return { 
    items,
    categoryTabs,
    activeCategoryCode,
    loaded,
    errors,
    initialFilter,
    initialItemsPage,
    fetchData,
    applySearchFilter,
    handleItemsOffset,
    handleCategoryChange,
    selectedAttachmentIds,
    selectedAttachments,
    hasVisibleAttachments,
    hasSelectedAttachments,
    isAllVisibleSelected,
    toggleAttachmentSelection,
    toggleAllVisibleSelection,
  }
}
